import React, { useState, useEffect, useCallback } from 'react';
import { generate } from '@pdfme/generator';
import { cloneDeep, Template } from '@pdfme/common';
import { getFontsData } from '../helper';
import { getPlugins } from '../plugins';

// Types
interface TemplateField {
  name: string;
  type: string;
  position: { x: number; y: number };
  width?: number;
  height?: number;
  fontSize?: number;
  fontColor?: string;
  content?: string;
  [key: string]: any;
}

interface TemplateData {
  id: string;
  name: string;
  template: Template;
  createdAt: string;
  updatedAt: string;
  pages: number;
  totalFields: number;
}

interface FormData {
  [key: string]: string;
}

const Preview: React.FC = () => {
  const [templates, setTemplates] = useState<TemplateData[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [currentTemplate, setCurrentTemplate] = useState<Template | null>(null);
  const [formData, setFormData] = useState<FormData>({});
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Load templates from localStorage
  useEffect(() => {
    const loadTemplates = () => {
      try {
        // Try multiple localStorage keys where templates might be stored
        const keys = ['templates', 'template', 'previewTemplate', 'pdf_template'];
        let foundTemplate = null;
        
        for (const key of keys) {
          const saved = localStorage.getItem(key);
          if (saved) {
            try {
              const parsed = JSON.parse(saved);
              
              if (Array.isArray(parsed)) {
                // Array of templates
                setTemplates(parsed);
                if (parsed.length > 0) {
                  setSelectedTemplateId(parsed[0].id);
                  setCurrentTemplate(parsed[0].template);
                  initializeFormData(parsed[0].template);
                }
                foundTemplate = parsed;
                break;
              } else if (parsed.schemas) {
                // Single template
                const templateData: TemplateData = {
                  id: 'default',
                  name: 'Current Template',
                  template: parsed,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                  pages: parsed.schemas.length,
                  totalFields: parsed.schemas.flat().length
                };
                setTemplates([templateData]);
                setSelectedTemplateId('default');
                setCurrentTemplate(parsed);
                initializeFormData(parsed);
                foundTemplate = [templateData];
                break;
              }
            } catch (e) {
              console.warn(`Failed to parse ${key}:`, e);
              continue;
            }
          }
        }
        
        if (!foundTemplate) {
          console.warn('No templates found in localStorage');
        }
      } catch (error) {
        console.error('Failed to load templates:', error);
      }
    };

    loadTemplates();
  }, []);

  // Initialize form data based on template fields
  const initializeFormData = (template: Template) => {
    const initialData: FormData = {};
    
    template.schemas.forEach((page, pageIndex) => {
      page.forEach(field => {
        // Initialize with existing content or default values
        if (field.type === 'table') {
          // For table fields, use sample data if no content exists
          initialData[field.name] = field.content || JSON.stringify([
            ["Alice", "New York", "Alice is a freelance web designer and developer"],
            ["Bob", "Paris", "Bob is a freelance illustrator and graphic designer"]
          ]);
        } else if (field.type === 'checkbox') {
          initialData[field.name] = field.content || 'false';
        } else if (field.type === 'qrcode') {
          initialData[field.name] = field.content || 'https://example.com';
        } else {
          initialData[field.name] = field.content || '';
        }
      });
    });
    
    setFormData(initialData);
  };

  // Handle template selection
  const handleTemplateChange = (templateId: string) => {
    const selected = templates.find(t => t.id === templateId);
    if (selected) {
      setSelectedTemplateId(templateId);
      setCurrentTemplate(selected.template);
      initializeFormData(selected.template);
    }
  };

  // Generate PDF using PDFme's generate function - EXACTLY like the designer
  const generatePDFWithPDFme = useCallback(async (): Promise<Uint8Array> => {
    if (!currentTemplate) {
      throw new Error('Template not loaded');
    }

    try {
      // Create a clean template copy
      const templateCopy = cloneDeep(currentTemplate);
      
      // PDFme expects inputs to be an array where each element corresponds to a set of data
      // For most cases, we use a single data object that gets applied to all fields
      const inputData: { [key: string]: any } = {};
      
      // Collect all field data from the form
      Object.keys(formData).forEach(fieldName => {
        const fieldValue = formData[fieldName];
        
        if (fieldValue !== undefined && fieldValue !== '') {
          // Find the field definition to determine its type
          let fieldType = 'text';
          templateCopy.schemas.forEach(pageSchema => {
            const field = pageSchema.find(f => f.name === fieldName);
            if (field) {
              fieldType = field.type;
            }
          });
          
          // Handle different field types appropriately
          switch (fieldType) {
            case 'table':
              try {
                // Parse JSON table data
                inputData[fieldName] = JSON.parse(fieldValue);
              } catch (e) {
                console.warn(`Invalid table data for ${fieldName}:`, fieldValue);
                inputData[fieldName] = [];
              }
              break;
            
            case 'checkbox':
              inputData[fieldName] = fieldValue === 'true';
              break;
            
            case 'image':
              // Only include if it's a valid data URL or not empty
              if (fieldValue.startsWith('data:image/') || fieldValue.trim() !== '') {
                inputData[fieldName] = fieldValue;
              }
              break;
            
            default:
              inputData[fieldName] = fieldValue;
              break;
          }
        }
      });

      // PDFme expects an array of input objects
      // For single document generation, we pass one object
      const inputs = [inputData];

      console.log('Generating PDF with PDFme...', {
        templatePages: templateCopy.schemas.length,
        fieldsPerPage: templateCopy.schemas.map(page => page.length),
        inputData: inputData,
        inputsLength: inputs.length
      });

      // Use PDFme's generate function with the exact same setup as Designer
      const pdf = await generate({
        template: templateCopy,
        inputs: inputs,
        options: {
          font: getFontsData(),
        },
        plugins: getPlugins(),
      });

      return pdf;
    } catch (error) {
      console.error('PDFme generation failed:', error);
      throw error;
    }
  }, [currentTemplate, formData]);

  // Preview PDF
  const handlePreview = async () => {
    if (!currentTemplate) return;
    
    setIsGenerating(true);
    try {
      const pdfBytes = await generatePDFWithPDFme();
      const blob = new Blob([pdfBytes as any], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      
      setPreviewUrl(url);
    } catch (error) {
      console.error('Preview generation failed:', error);
      if (error instanceof Error) {
        alert(`Failed to generate PDF preview: ${error.message}`);
      } else {
        alert('Failed to generate PDF preview: Unknown error');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // Download PDF
  const handleDownload = async () => {
    if (!currentTemplate) return;
    
    setIsGenerating(true);
    try {
      const pdfBytes = await generatePDFWithPDFme();
      const blob = new Blob([pdfBytes as any], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${templates.find(t => t.id === selectedTemplateId)?.name || 'document'}_${Date.now()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      if (error instanceof Error) {
        alert(`Failed to download PDF: ${error.message}`);
      } else {
        alert('Failed to download PDF: Unknown error');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // Sync with designer - pull latest template from designer localStorage
  const syncWithDesigner = () => {
    const designerTemplate = localStorage.getItem('template');
    const previewTemplate = localStorage.getItem('previewTemplate');
    
    const templateToUse = previewTemplate || designerTemplate;
    
    if (templateToUse) {
      try {
        const parsed = JSON.parse(templateToUse);
        if (parsed.schemas) {
          const templateData: TemplateData = {
            id: 'synced',
            name: 'Synced from Designer',
            template: parsed,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            pages: parsed.schemas.length,
            totalFields: parsed.schemas.flat().length
          };
          
          // Update templates list
          const updatedTemplates = templates.filter(t => t.id !== 'synced');
          updatedTemplates.unshift(templateData);
          setTemplates(updatedTemplates);
          
          // Select the synced template
          setSelectedTemplateId('synced');
          setCurrentTemplate(parsed);
          initializeFormData(parsed);
          
          alert('Template synced from designer successfully!');
        }
      } catch (error) {
        console.error('Failed to sync template:', error);
        alert('Failed to sync template from designer');
      }
    } else {
      alert('No template found in designer. Please create a template first.');
    }
  };

  // Save template
  const saveTemplate = () => {
    if (currentTemplate) {
      const templateName = prompt('Enter template name:');
      if (!templateName) return;

      const templateToSave: TemplateData = {
        id: Date.now().toString(),
        name: templateName,
        template: currentTemplate,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        pages: currentTemplate.schemas.length,
        totalFields: currentTemplate.schemas.flat().length
      };

      const updatedTemplates = [...templates, templateToSave];
      setTemplates(updatedTemplates);
      
      // Save to localStorage
      localStorage.setItem('templates', JSON.stringify(updatedTemplates));
      
      alert('Template saved successfully!');
    }
  };

  // Handle input changes
  const handleInputChange = (fieldName: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Auto-generate preview when data changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (currentTemplate && Object.keys(formData).length > 0) {
        handlePreview();
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [formData, currentTemplate]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Get all fields from template
  const getAllFields = () => {
    if (!currentTemplate) return [];
    
    const fields: (TemplateField & { pageNumber: number })[] = [];
    currentTemplate.schemas.forEach((page, pageIndex) => {
      page.forEach(field => {
        fields.push({ ...field, pageNumber: pageIndex + 1 });
      });
    });
    
    return fields;
  };

  const allFields = getAllFields();

  // Render form input based on field type
  const renderFormInput = (field: TemplateField) => {
    const fieldValue = formData[field.name] || '';
    
    switch (field.type) {
      case 'table':
        return (
          <div>
            <textarea
              value={fieldValue}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              placeholder='[["Name","City","Description"],["Alice","New York","Designer"],["Bob","Paris","Illustrator"]]'
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter JSON array format: [["row1col1","row1col2"],["row2col1","row2col2"]]
            </p>
          </div>
        );
      
      case 'text':
      case 'multiVariableText':
        if (field.name.includes('content') || field.name.includes('description') || (field.content && field.content.length > 50)) {
          return (
            <textarea
              value={fieldValue}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={field.content || 'Enter text content...'}
            />
          );
        } else {
          return (
            <input
              type="text"
              value={fieldValue}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={field.content || 'Enter text...'}
            />
          );
        }
      
      case 'image':
        return (
          <div>
            <textarea
              value={fieldValue}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs"
              placeholder="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=="
            />
            <p className="text-xs text-gray-500 mt-1">
              Paste base64 image data URL (data:image/...)
            </p>
          </div>
        );
      
      case 'checkbox':
        return (
          <select
            value={fieldValue}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="false">☐ Unchecked</option>
            <option value="true">☑ Checked</option>
          </select>
        );
      
      case 'select':
        return (
          <select
            value={fieldValue}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {field.options?.map((option: string) => (
              <option key={option} value={option}>{option}</option>
            )) || <option value={fieldValue}>{fieldValue || 'No options available'}</option>}
          </select>
        );
      
      case 'date':
        return (
          <input
            type="date"
            value={fieldValue}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        );
      
      case 'time':
        return (
          <input
            type="time"
            value={fieldValue}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        );
      
      case 'qrcode':
        return (
          <input
            type="text"
            value={fieldValue}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="https://example.com or any text"
          />
        );
      
      default:
        return (
          <input
            type="text"
            value={fieldValue}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={field.content || `Enter ${field.type} content...`}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
            <h1 className="text-3xl font-bold">PDF Preview</h1>
            <p className="text-blue-100 mt-2">Live preview using PDFme generator - identical to designer output</p>
          </div>

          {/* Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
            {/* Form Section */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-800">Edit Content</h2>
              
              {/* Template Selector */}
              {/* {templates.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Template:
                  </label>
                  <select
                    value={selectedTemplateId}
                    onChange={(e) => handleTemplateChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {templates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name} ({template.pages} pages, {template.totalFields} fields)
                      </option>
                    ))}
                  </select>
                </div>
              )} */}

              {/* Dynamic Form Fields */}
              <div className="max-h-96 overflow-y-auto space-y-4">
                {allFields.length > 0 ? allFields.map((field, index) => (
                  <div key={`${field.name}-${index}`} className="border-b pb-3">
                    <div className="flex justify-between items-center mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        {field.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </label>
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        Page {field.pageNumber} • {field.type}
                      </span>
                    </div>
                    {renderFormInput(field)}
                  </div>
                )) : (
                  <div className="text-center text-gray-500 py-8">
                    <p>No fields found in template</p>
                    <button
                      onClick={syncWithDesigner}
                      className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                    >
                      Sync from Designer
                    </button>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handlePreview}
                  disabled={isGenerating || !currentTemplate}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {isGenerating ? 'Generating...' : 'Preview PDF'}
                </button>
                <button
                  onClick={handleDownload}
                  disabled={isGenerating || !currentTemplate}
                  className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  Download PDF
                </button>
              </div>

              {/* Sync and Save Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={syncWithDesigner}
                  className="flex-1 bg-orange-600 text-white py-2 px-4 rounded-md hover:bg-orange-700 transition-colors"
                >
                  Sync from Designer
                </button>
                <button
                  onClick={saveTemplate}
                  disabled={!currentTemplate}
                  className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 disabled:opacity-50 transition-colors"
                >
                  Save Template
                </button>
              </div>

              {/* Template Info */}
              {currentTemplate && (
                <div className="mt-4 p-3 bg-gray-100 rounded-md">
                  <p className="text-sm text-gray-600">
                    <strong>Pages:</strong> {currentTemplate.schemas.length} | 
                    <strong> Total Fields:</strong> {allFields.length}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Using PDFme generator with same plugins and fonts as Designer
                  </p>
                </div>
              )}
            </div>

            {/* Preview Section */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-800">PDF Preview</h2>
              
              <div className="border border-gray-300 rounded-lg overflow-hidden h-96 bg-gray-100">
                {previewUrl ? (
                  <object
                    data={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=1&page=1&zoom=FitH`}
                    type="application/pdf"
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                  >
                    <iframe
                      src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=1&page=1&zoom=FitH`}
                      width="100%"
                      height="100%"
                      style={{ border: 'none' }}
                      title="PDF Preview"
                    />
                  </object>
                ) : (
                  <div className="flex items-center justify-center h-full bg-gray-50 text-gray-500">
                    {isGenerating ? (
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                        <p>Generating PDF with PDFme...</p>
                      </div>
                    ) : (
                      <div className="text-center">
                        <p className="mb-2">
                          {templates.length === 0 
                            ? 'No templates found.' 
                            : 'Edit content above to see live preview'
                          }
                        </p>
                        {templates.length === 0 && (
                          <button
                            onClick={syncWithDesigner}
                            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                          >
                            Sync from Designer
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Debug and Alternative Options */}
              {previewUrl && (
                <div className="flex gap-2 justify-center text-sm">
                  <button
                    onClick={() => window.open(previewUrl, '_blank')}
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Open in New Tab
                  </button>
                  <span className="text-gray-400">|</span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(previewUrl);
                      alert('PDF URL copied to clipboard');
                    }}
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Copy URL
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Preview;